/**
 * Bundle Builder JavaScript
 * Handles all interactive functionality for the Custom Bundle Builder
 */
(function () {
  "use strict";
  class BundleBuilder {
    constructor() {
      this.isLoading = false;
      this.currentPage = 1;
      this.perPage = 20;
      this.currentTier = null;
      this.selectedProducts = new Map();
      this.deliveryType = "subscribe";
      const sectionRoot = document.querySelector(".bundle-builder-section");
      this.variantHandling = sectionRoot && sectionRoot.dataset && sectionRoot.dataset.variantHandling ? sectionRoot.dataset.variantHandling : "popup";
      this.showOutOfStock = sectionRoot && sectionRoot.dataset && sectionRoot.dataset.showOutOfStock ? sectionRoot.dataset.showOutOfStock === "true" : false;
      this.collectionsData = this.loadCollectionsData();
      this.isMobile = window.innerWidth <= 1024;
      this.init();
    }

    showTierErrorDialog(targetTierQuantity, currentItemCount) {
      const dialog = document.getElementById("tier-error-dialog");
      const messageElement = document.getElementById("tier-error-message");

      const itemsToRemove = currentItemCount - targetTierQuantity;
      const itemText = itemsToRemove === 1 ? "item" : "items";

      const message = `You have ${currentItemCount} items in your bundle, but you're trying to switch to ${targetTierQuantity} packs. Please remove ${itemsToRemove} ${itemText} from the bundle preview section to switch to ${targetTierQuantity} packs.`;

      messageElement.textContent = message;
      dialog.style.display = "flex";

      // Auto-close on backdrop click
      dialog.addEventListener("click", (e) => {
        if (e.target === dialog) {
          dialog.style.display = "none";
        }
      });

      // Close on Escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && dialog.style.display === "flex") {
          dialog.style.display = "none";
        }
      });
    }

    loadCollectionsData() {
      try {
        const dataElement = document.getElementById("bundle-builder-collections-data");
        if (dataElement) {
          return JSON.parse(dataElement.textContent);
        }
      } catch (error) {
        console.error("Error loading collections data:", error);
      }
      return { collections: {} };
    }

    loadBundleProductData() {
      try {
        const dataElement = document.getElementById("bundle-product-data");
        if (dataElement) {
          return JSON.parse(dataElement.textContent);
        }
      } catch (error) {
        console.error("Error loading bundle product data:", error);
      }
      return null;
    }

    initializeDynamicTiers() {
      const bundleProductData = this.loadBundleProductData();
      if (!bundleProductData || !bundleProductData.variants) {
        return; // No dynamic product data available
      }

      const container = document.getElementById("bundle-tiers-container");
      if (!container) {
        return;
      }

      // Clear existing content
      container.innerHTML = "";

      // Generate tier options from product variants
      bundleProductData.variants.forEach((variant, index) => {
        const tierElement = document.createElement("div");
        tierElement.className = "bundle-tier-option";
        tierElement.dataset.tierId = variant.id;

        // Extract quantity from variant title (e.g., "5 Pack" -> 5)
        const quantityMatch = variant.title.match(/(\d+)/);
        const quantity = quantityMatch ? parseInt(quantityMatch[1]) : index + 1;
        tierElement.dataset.tierQuantity = quantity;

        // Format price
        const price = this.formatMoney((parseFloat(variant.price).toFixed()) / 100);
        const comparePrice = variant.compare_at_price > 0 ? this.formatMoney(variant.compare_at_price / 100) : null;
        const additionalText = document.querySelector(".bundle-builder-section").dataset.additionalTierText ? ` + ${document.querySelector(".bundle-builder-section").dataset.additionalTierText}` : '';
        // Create tier content
        tierElement.innerHTML = `
        <div class="tier-title">${variant.title}</div>
        <div class="tier-subtitle">${comparePrice ? comparePrice : price} ${additionalText}</div>
      `;

        // Apply default styling
        tierElement.style.cssText = `
        background-color: var(--bundle-builder-tier-options-bg);
        color: var(--bundle-builder-tier-options-text);
        border-color: var(--bundle-builder-tier-options-border);
        --selected-bg: #e8f5e8;
        --selected-text: #2e7d32;
        --selected-border: #2e7d32;
      `;

        container.appendChild(tierElement);
      });

      console.log("Dynamic bundle tiers initialized from product:", bundleProductData.title);
    }

    init() {
      this.bindEvents();
      this.updateProgress();
      this.updateBundleSummary();
      this.handleResize();

      // Initialize dynamic bundle tiers if available
      this.initializeDynamicTiers();

      // Load the initial active collection
      this.loadInitialCollection();

      // Update mobile cart visibility on initial load
      this.updateMobileCartVisibility();

      // Update cart preview height for mobile
      this.updateCartPreviewHeight();

      // Detect when CSS is loaded and show content
      this.detectCSSLoaded();

      // Initialize infinite scroll
      // /this.initInfiniteScroll();
    }

    // Detect when CSS is loaded and show content smoothly
    detectCSSLoaded() {
      const section = document.querySelector(".bundle-builder-section");
      if (!section) return;

      // Check if CSS is already loaded
      if (document.readyState === "complete") {
        this.showContent();
      } else {
        // Wait for CSS to load
        window.addEventListener("load", () => {
          this.showContent();
        });
      }
    }

    // Calculate and set dynamic cart preview height
    updateCartPreviewHeight() {
      if (!this.isMobile) return;

      const cartPreview = document.querySelector(".bundle-builder-right-pane");
      const productGrid = document.querySelector(".product-grid-section");

      if (cartPreview && productGrid) {
        const cartHeight = cartPreview.offsetHeight + 10; // Add 10px to calculated height
        const root = document.documentElement;
        root.style.setProperty("--cart-preview-height", `${cartHeight}px`);
      }
    }

    showContent() {
      const section = document.querySelector(".bundle-builder-section");
      if (section) {
        // Add CSS loaded class to show content
        section.classList.add("css-loaded");

        // Hide the initial loading overlay after everything is loaded
        this.hideInitialLoading();
      }
    }

    // Loading overlay methods
    hideInitialLoading() {
      const loadingOverlay = document.getElementById("bundle-builder-loading");
      if (loadingOverlay) {
        // Add a small delay to ensure everything is rendered
        setTimeout(() => {
          loadingOverlay.classList.add("hidden");
        }, 500);
      }
    }

    showCheckoutLoading() {
      const checkoutLoading = document.getElementById("checkout-loading");
      if (checkoutLoading) {
        checkoutLoading.classList.add("visible");
      }
    }

    hideCheckoutLoading() {
      const checkoutLoading = document.getElementById("checkout-loading");
      if (checkoutLoading) {
        checkoutLoading.classList.remove("visible");
      }
    }

    loadInitialCollection() {
      // Find the first active collection tab
      const activeTab = document.querySelector('.collection-tab[data-active="true"]');
      if (activeTab) {
        const collectionHandle = activeTab.dataset.collectionHandle;
        this.loadCollectionProducts(collectionHandle);
      }
    }

    async loadMoreProducts() {
      try{
        if(this.isLoading) return;
        this.showLoadingIndicator();
          this.isLoading = true
          this.currentPage++;
          
          const activeTab = document.querySelector('.collection-tab[data-active="true"]');
          let collectionHandle;
          if (activeTab) {
            collectionHandle = activeTab.dataset.collectionHandle;
          }
          //const res = await fetch(`/collections/${collectionHandle}/products.json?page=${this.currentPage}&limit=${this.perPage}`);
          const res = await fetch(`/collections/${collectionHandle}?view=json&page=${this.currentPage}&limit=${this.perPage}`);
          const data = await res.json();
          console.log(data)
          if (data.products && data.products.length > 0) {
            this.addProductsToGrid(data.products); // reuse your existing function
          }
          this.isLoading = false;
          this.hideLoadingIndicator();
      }catch(e){
        console.log("Error", e.message);
        
      }
    }

    bindEvents() {
      const sentinel = document.getElementById("sentinel");
      
      const observer = new IntersectionObserver(
        async (entries) => {
            if (entries[0].isIntersecting) {
              await this.loadMoreProducts();
            }
          },
          {
            root: document.querySelector(".bundle-builder-left-pane"),
            threshold: 1.0,
          }
      );

      observer.observe(sentinel);

      // Bundle tier selection
      document.addEventListener("click", (e) => {
        if (e.target.closest(".bundle-tier-option")) {
          this.selectTier(e.target.closest(".bundle-tier-option"));
        }
      });

      // Preload images on product card hover
      document.addEventListener(
        "mouseenter",
        (e) => {
          if (e.target.closest(".product-card")) {
            this.preloadProductImages(e.target.closest(".product-card"));
          }
        },
        true
      );

      // Collection tab switching
      document.addEventListener("click", (e) => {
        if (e.target.closest(".collection-tab")) {
          this.switchCollection(e.target.closest(".collection-tab"));
        }
      });

      // Add to bundle button
      document.addEventListener("click", (e) => {
        if (e.target.closest(".add-to-bundle-btn")) {
          this.handleAddToBundle(e.target.closest(".add-to-bundle-btn"));
        }
      });

      // Quantity buttons
      document.addEventListener("click", (e) => {
        if (e.target.closest(".quantity-btn")) {
          this.handleQuantityChange(e.target.closest(".quantity-btn"));
        }
      });

      // Variant dropdown change
      document.addEventListener("change", (e) => {
        if (e.target.classList.contains("variant-dropdown")) {
          this.handleVariantChange(e.target);
        }
      });

      // Delivery option selection
      document.addEventListener("click", (e) => {
        if (e.target.closest(".delivery-option")) {
          this.selectDeliveryOption(e.target.closest(".delivery-option"));
        }
      });

      // Product details button
      document.addEventListener("click", (e) => {
        if (e.target.closest(".product-details-btn")) {
          this.openProductDetails(e.target.closest(".product-details-btn"));
        }
      });

      // Remove item from bundle
      document.addEventListener("click", (e) => {
        if (e.target.closest(".remove-item")) {
          this.removeFromBundle(e.target.closest(".remove-item"));
        }
      });

      // Checkout button
      document.addEventListener("click", (e) => {
        if (e.target.closest(".checkout-button")) {
          this.handleCheckout(e.target.closest(".checkout-button"));
        }
      });

      // Variant popup close button
      document.addEventListener("click", (e) => {
        if (e.target.closest(".variant-popup-close")) {
          const popup = e.target.closest(".variant-popup-overlay");
          popup.classList.remove("visible");
          setTimeout(() => {
            popup.classList.add("bundle-builder-hidden");
          }, 300);

          // Reset variant selection if this was the current popup
          if (this.currentVariantProductCard && popup.closest(".product-card") === this.currentVariantProductCard) {
            this.currentVariantProduct = null;
            this.currentVariantProductCard = null;
            this.selectedVariant = null;
          }
        }
      });

      // Variant add to bundle button
      document.addEventListener("click", (e) => {
        if (e.target.closest(".variant-add-to-bundle-btn")) {
          this.handleVariantAddToBundle();
        }
      });

      // Variant quantity buttons
      document.addEventListener("click", (e) => {
        if (e.target.closest(".variant-quantity-btn")) {
          this.handleVariantQuantityChange(e.target.closest(".variant-quantity-btn"));
        }
      });

      // Close variant popup when clicking overlay
      document.addEventListener("click", (e) => {
        if (e.target.classList.contains("variant-popup-overlay")) {
          // Close only the specific popup that was clicked
          e.target.classList.remove("visible");
          setTimeout(() => {
            e.target.classList.add("bundle-builder-hidden");
          }, 300);

          // Reset variant selection if this was the current popup
          if (this.currentVariantProductCard && e.target.closest(".product-card") === this.currentVariantProductCard) {
            this.currentVariantProduct = null;
            this.currentVariantProductCard = null;
            this.selectedVariant = null;
          }
        }
      });

      // Handle window resize for mobile detection
      window.addEventListener("resize", () => {
        this.handleResize();
      });

      const bundleItemList = document.querySelector(".bundle-items");
      if (bundleItemList) {
        bundleItemList.addEventListener(
          "scroll",
          () => {
            this.updateScrollIndicator();
          },
          true
        );
      }

      // Handle scroll events for bundle items
      // document.addEventListener('scroll', (e) => {
      //   //if(!this.isMobile){
      //     if (e.target && typeof e.target.closest === 'function' && e.target.closest('.bundle-items')) {
      //       this.updateScrollIndicator();
      //     }
      //   //}
      // }, true);

      // Handle mobile cart expansion and close button
      document.addEventListener("click", (e) => {
        if (e.target.closest(".mobile-cart-expand")) {
          const expandButton = e.target.closest(".mobile-cart-expand");
          const isFullView = document.querySelector(".bundle-builder-right-pane")?.classList.contains("full-view");

          if (isFullView) {
            // In full view mode, clicking the button should trigger checkout
            this.handleCheckout(expandButton);
          } else {
            // In compact view mode, clicking the button should expand to full view
            this.toggleMobileCartExpansion();
          }
        }

        // Handle close button clicks
        if (e.target.closest(".mobile-cart-close")) {
          this.closeMobileCartFullView();
        }
      });
    }

    selectTier(tierElement) {
      const newTierQuantity = parseInt(tierElement.dataset.tierQuantity);
      const currentItemCount = this.selectedProducts.size;

      // Prevent switching to smaller tier if user has too many items
      if (currentItemCount > newTierQuantity) {
        // /const message = `Looks like you're switching to ${newTierQuantity} packs but you already have ${currentItemCount} items in your bundle. Please remove ${currentItemCount - newTierQuantity} item(s) from the bundle preview section to switch to ${newTierQuantity} packs.`;
        this.showTierErrorDialog(newTierQuantity, currentItemCount);
        return; // Don't change tier - stay on current tier
      }

      // Update current tier
      this.currentTier = {
        id: tierElement.dataset.tierId,
        quantity: parseInt(tierElement.dataset.tierQuantity),
      };

      // Update UI selection
      this.updateTierSelectionUI(tierElement.dataset.tierId);

      // Update progress indicator and checkout button visibility
      this.updateProgress();
    }

    switchCollection(tabElement) {
      // Remove active class from all tabs
      document.querySelectorAll(".collection-tab").forEach((tab) => {
        tab.removeAttribute("data-active");
      });

      // Add active class to clicked tab
      tabElement.setAttribute("data-active", "true");

      // Reset infinite scroll for new collection
      this.resetInfiniteScroll();

      // Get the product grid for smooth transitions
      const productGrid = document.querySelector(".product-grid");
      if (!productGrid) {
        // Fallback to immediate loading if no grid found
        const collectionHandle = tabElement.dataset.collectionHandle;
        this.loadCollectionProducts(collectionHandle);
        return;
      }

      // Add fade-out effect
      productGrid.style.opacity = "0.5";
      productGrid.style.transform = "translateY(-10px)";

      // Load products for this collection after a brief delay
      const collectionHandle = tabElement.dataset.collectionHandle;
      setTimeout(() => {
        // Preload images for the new collection before rendering
        this.preloadCollectionImages(collectionHandle);

        // Load products with loading placeholders
        this.loadCollectionProductsWithPlaceholders(collectionHandle);

        // Fade back in with slide effect
        setTimeout(() => {
          productGrid.style.opacity = "1";
          productGrid.style.transform = "translateY(0)";
        }, 50); // Small delay to ensure content is rendered
      }, 150); // Match the timing used for variant image transitions
    }

    handleAddToBundle(buttonElement) {
      // Check if a tier is selected first
      if (!this.currentTier) {
        this.showTierSelectionError();
        return;
      }

      const productCard = buttonElement.closest(".product-card");
      const productId = productCard.dataset.productId;

      // Get product data to check for variants
      const productData = this.getProductData(productId);

      // Check if product has variants and validate selection
      if (productData && productData.has_variants === true) {
        // Validate variant selection
        const variantDropdown = productCard.querySelector(".variant-dropdown");
        const selectedVariantId = variantDropdown.value;

        if (!selectedVariantId) {
          // Show error message for missing variant selection
          this.showVariantSelectionError(productCard);
          return;
        }

        // Add product with selected variant
        this.addProductWithVariant(productCard, selectedVariantId);
      } else {
        // No variants - add directly to bundle
        this.addProductDirectly(productCard);
      }
    }

    handleVariantChange(dropdownElement) {
      const productCard = dropdownElement.closest(".product-card");
      const selectedVariantId = dropdownElement.value;

      if (!selectedVariantId) {
        // No variant selected, reset to default
        this.resetProductCardToDefault(productCard);
        return;
      }

      const productId = productCard.dataset.productId;
      const productData = this.getProductData(productId);

      if (!productData) {
        console.error("Product data not found");
        return;
      }

      // Find the selected variant
      const selectedVariant = productData.variants.find((v) => v.id.toString() === selectedVariantId.toString());
      if (!selectedVariant) {
        console.error("Selected variant not found");
        return;
      }

      // Update product image if variant has a different image
      if (selectedVariant.image) {
        const productImage = productCard.querySelector("img");

        // Add fade-out effect
        productImage.style.opacity = "0.3";

        // Update src first for immediate transition
        productImage.src = selectedVariant.image;

        // Update srcset and sizes after a brief delay
        setTimeout(() => {
          if (selectedVariant.image_srcset) {
            productImage.srcset = selectedVariant.image_srcset;
          } else {
            // Generate srcset if not provided
            const baseUrl = selectedVariant.image;
            const url200 = baseUrl.replace(/width=\d+/, "width=200");
            const url400 = baseUrl.replace(/width=\d+/, "width=400");
            const url600 = baseUrl.replace(/width=\d+/, "width=600");
            productImage.srcset = `${url200} 200w, ${url400} 400w, ${url600} 600w`;
          }
          productImage.sizes = "(max-width: 480px) 50vw, (max-width: 749px) 33vw, 25vw";

          // Fade back in
          productImage.style.opacity = "1";
        }, 150);
      }

      // Update product name to include variant title
      const productName = productCard.querySelector(".product-name");
      productName.textContent = `${productData.title} - ${selectedVariant.title}`;

      // Update product price
      const productPrice = productCard.querySelector(".product-price");
      const price = this.formatMoney(selectedVariant.price / 100);
      const comparePrice = selectedVariant.compare_at_price > 0 ? this.formatMoney(selectedVariant.compare_at_price / 100) : null;

      if (comparePrice) {
        productPrice.innerHTML = `<span class="original-price">${comparePrice}</span><span class="discount-price">${price}</span>`;
      } else {
        productPrice.textContent = price;
      }

      // Update product card data attributes
      productCard.dataset.selectedVariantId = selectedVariantId;
      productCard.dataset.productPrice = (selectedVariant.price / 100).toString();
    }

    resetProductCardToDefault(productCard) {
      const productId = productCard.dataset.productId;
      const productData = this.getProductData(productId);

      if (!productData) return;

      // Reset to default variant or first available variant
      const defaultVariant = productData.variants.find((v) => v.id.toString() === productData.default_variant_id?.toString()) || productData.variants[0];

      if (defaultVariant) {
        // Update product image
        const productImage = productCard.querySelector("img");
        const imageUrl = defaultVariant.image || productData.featured_image || productImage.src;

        // Add fade-out effect
        productImage.style.opacity = "0.3";

        // Update src first for immediate transition
        productImage.src = imageUrl;

        // Update srcset and sizes after a brief delay
        setTimeout(() => {
          if (defaultVariant.image_srcset || productData.featured_image_srcset) {
            productImage.srcset = defaultVariant.image_srcset || productData.featured_image_srcset;
          } else if (imageUrl && imageUrl !== productImage.src) {
            // Generate srcset if not provided
            const baseUrl = imageUrl;
            const url200 = baseUrl.replace(/width=\d+/, "width=200");
            const url400 = baseUrl.replace(/width=\d+/, "width=400");
            const url600 = baseUrl.replace(/width=\d+/, "width=600");
            productImage.srcset = `${url200} 200w, ${url400} 400w, ${url600} 600w`;
          }
          productImage.sizes = "(max-width: 480px) 50vw, (max-width: 749px) 33vw, 25vw";

          // Fade back in
          productImage.style.opacity = "1";
        }, 150);

        // Reset product name
        const productName = productCard.querySelector(".product-name");
        productName.textContent = productData.title;

        // Reset product price
        const productPrice = productCard.querySelector(".product-price");
        const price = this.formatMoney(defaultVariant.price / 100);
        const comparePrice = defaultVariant.compare_at_price > 0 ? this.formatMoney(defaultVariant.compare_at_price / 100) : null;

        if (comparePrice) {
          productPrice.innerHTML = `<span class="original-price">${comparePrice}</span><span class="discount-price">${price}</span>`;
        } else {
          productPrice.textContent = price;
        }

        // Reset data attributes
        productCard.dataset.selectedVariantId = defaultVariant.id;
        productCard.dataset.productPrice = (defaultVariant.price / 100).toString();
      }
    }

    showVariantSelectionError(productCard) {
      // Create or show error message
      let errorMessage = productCard.querySelector(".variant-selection-error");
      if (!errorMessage) {
        errorMessage = document.createElement("div");
        errorMessage.className = "variant-selection-error";
        errorMessage.innerHTML = `
         <div class="error-message">
           <span class="error-icon">⚠️</span>
           <span class="error-text">Please select a variant before adding to bundle.</span>
         </div>
       `;

        // Insert after the variant selector
        const variantSelector = productCard.querySelector(".variant-selector");
        if (variantSelector) {
          variantSelector.parentNode.insertBefore(errorMessage, variantSelector.nextSibling);
        }
      }

      // Show the error message
      errorMessage.style.display = "block";

      // Auto-hide after 3 seconds
      setTimeout(() => {
        errorMessage.style.display = "none";
      }, 3000);
    }

    addProductWithVariant(productCard, selectedVariantId) {
      const productId = productCard.dataset.productId;
      const productData = this.getProductData(productId);

      // Find the selected variant
      const selectedVariant = productData.variants.find((v) => v.id.toString() === selectedVariantId.toString());
      if (!selectedVariant) {
        console.error("Selected variant not found");
        return;
      }

      // Get quantity from the product card
      const quantityDisplay = productCard.querySelector(".quantity-display");
      const quantity = parseInt(quantityDisplay.textContent) || 1;

      // Update product card with variant information
      productCard.dataset.selectedVariantId = selectedVariantId;
      productCard.dataset.productPrice = (selectedVariant.price / 100).toString();

      // Update product image if variant has a different image
      if (selectedVariant.image) {
        const productImage = productCard.querySelector("img");

        // Add fade-out effect
        productImage.style.opacity = "0.3";

        // Update src first for immediate transition
        productImage.src = selectedVariant.image;

        // Update srcset and sizes after a brief delay
        setTimeout(() => {
          if (selectedVariant.image_srcset) {
            productImage.srcset = selectedVariant.image_srcset;
          } else {
            // Generate srcset if not provided
            const baseUrl = selectedVariant.image;
            const url200 = baseUrl.replace(/width=\d+/, "width=200");
            const url400 = baseUrl.replace(/width=\d+/, "width=400");
            const url600 = baseUrl.replace(/width=\d+/, "width=600");
            productImage.srcset = `${url200} 200w, ${url400} 400w, ${url600} 600w`;
          }
          productImage.sizes = "(max-width: 480px) 50vw, (max-width: 749px) 33vw, 25vw";

          // Fade back in
          productImage.style.opacity = "1";
        }, 150);
      }

      // Update product name to include variant title
      const productName = productCard.querySelector(".product-name");
      productName.textContent = `${productData.title} - ${selectedVariant.title}`;

      // Update product price
      const productPrice = productCard.querySelector(".product-price");
      const price = this.formatMoney(selectedVariant.price / 100);
      const comparePrice = selectedVariant.compare_at_price > 0 ? this.formatMoney(selectedVariant.compare_at_price / 100) : null;

      if (comparePrice) {
        productPrice.innerHTML = `<span class="original-price">${comparePrice}</span><span class="discount-price">${price}</span>`;
      } else {
        productPrice.textContent = price;
      }

      // Add product to selection and update UI
      productCard.classList.add("selected");
      this.addProductToSelection(productCard, quantity);

      // Reset quantity selector to 1 after adding items
      quantityDisplay.textContent = "1";

      // Update bundle preview and progress
      this.updateBundlePreview();
      this.updateProgress();
      this.updateBundleSummary();

      // Update mobile cart visibility
      this.updateMobileCartVisibility();
    }

    addProductDirectly(productCard) {
      const productId = productCard.dataset.productId;

      // Get quantity from the product card (always visible now)
      const quantityDisplay = productCard.querySelector(".quantity-display");
      const quantity = parseInt(quantityDisplay.textContent) || 1;

      // Add product to selection and update UI
      productCard.classList.add("selected");
      this.addProductToSelection(productCard, quantity);

      // Reset quantity selector to 1 after adding items
      quantityDisplay.textContent = "1";

      // Update bundle preview and progress
      this.updateBundlePreview();
      this.updateProgress();
      this.updateBundleSummary();

      // Update mobile cart visibility
      this.updateMobileCartVisibility();
    }

    getProductData(productId) {
      // Find product data from collections
      for (const collectionHandle in this.collectionsData.collections) {
        const collection = this.collectionsData.collections[collectionHandle];
        const product = collection.products.find((p) => p.id.toString() === productId.toString());
        if (product) {
          return product;
        }
      }
      return null;
    }

    showVariantPopup(productData, productCard) {
      // Check if a tier is selected first
      if (!this.currentTier) {
        this.showTierSelectionError();
        return;
      }

      this.currentVariantProduct = productData;
      this.currentVariantProductCard = productCard;
      this.selectedVariant = null;

      // Find the popup within this specific product card
      const popup = productCard.querySelector(".variant-popup-overlay");

      // Update the title to use the product name
      const popupTitle = popup.querySelector(".variant-popup-title");
      popupTitle.textContent = `${productData.title}`;

      // Generate variant options
      this.populateVariantOptions(productData, popup);

      // Show popup
      popup.classList.remove("bundle-builder-hidden");
      setTimeout(() => {
        popup.classList.add("visible");
      }, 10);
    }

    createVariantCard(variant, productData, productIndex, variantIndex) {
      const variantCard = document.createElement("div");
      variantCard.className = "product-card variant-card";
      variantCard.dataset.productId = variant.id;
      variantCard.dataset.productHandle = productData.handle;
      variantCard.dataset.variantId = variant.id;
      variantCard.dataset.productPrice = (variant.price / 100).toString();
      variantCard.dataset.hasVariants = "false"; // This is now a variant card, so no further variants

      // Add out-of-stock class if variant is unavailable
      if (!variant.available) {
        variantCard.classList.add("out-of-stock");
      }

      // Get variant image or fallback to product image with responsive srcset
      const imageUrl =
        variant.image ||
        productData.featured_image ||
        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23f5f5f5"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial, sans-serif" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';

      // Create srcset for responsive images
      let srcset = variant.image_srcset || productData.featured_image_srcset || "";
      let sizes = "(max-width: 480px) 50vw, (max-width: 749px) 33vw, 25vw";

      // Format variant price
      const price = this.formatMoney(variant.price / 100);
      const comparePrice = variant.compare_at_price > 0 ? this.formatMoney(variant.compare_at_price / 100) : null;

      // Create variant title
      const variantTitle = `${productData.title} - ${variant.title}`;

      // Determine button text and state
      const buttonText = variant.available ? "Add" : "Out of Stock";
      const buttonDisabled = !variant.available;

      variantCard.innerHTML = `
        <!-- Selected Badge -->
        <div class="selected-badge">${productIndex}.${variantIndex}</div>
        
        <!-- Product Image -->
        <div class="product-image-container">
        <img src="${imageUrl}" 
               ${srcset ? `srcset="${srcset}"` : ""}
               sizes="${sizes}"
             alt="${variantTitle}"
             width="200"
             height="200"
               loading="lazy"
               class="product-image">
        </div>
        
        <!-- Product Details -->
        <div class="product-name">${variantTitle}</div>
        
        <!-- Product Price -->
        <div class="product-price">
          ${comparePrice ? `<span class="original-price">${comparePrice}</span>` : ""}
          <span class="${comparePrice ? "discount-price" : ""}">${price}</span>
        </div>
        
        <!-- Add Button (Initial State) -->
        <button class="add-to-bundle-btn" data-product-id="${variant.id}" ${buttonDisabled ? "disabled" : ""}>
          ${buttonText}
        </button>
        
        <!-- Quantity Selector (Hidden Initially) -->
        <div class="quantity-selector bundle-builder-hidden">
          <button class="quantity-btn" data-action="decrease">-</button>
          <span class="quantity-display">1</span>
          <button class="quantity-btn" data-action="increase">+</button>
        </div>
        
        <!-- View Details Button 
        <button class="product-details-btn" 
                data-product-url="${productData.url}"
                data-product-id="${variant.id}">
          View Details
        </button> -->
      `;

      return variantCard;
    }

    createVariantCardWithPlaceholder(variant, productData, productIndex, variantIndex) {
      const variantCard = document.createElement("div");
      variantCard.className = "product-card variant-card";
      variantCard.dataset.productId = variant.id;
      variantCard.dataset.productHandle = productData.handle;
      variantCard.dataset.variantId = variant.id;
      variantCard.dataset.productPrice = (variant.price / 100).toString();
      variantCard.dataset.hasVariants = "false"; // This is now a variant card, so no further variants

      // Add out-of-stock class if variant is unavailable
      if (!variant.available) {
        variantCard.classList.add("out-of-stock");
      }

      // Get variant image or fallback to product image with responsive srcset
      const imageUrl =
        variant.image ||
        productData.featured_image ||
        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23f5f5f5"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial, sans-serif" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';

      // Create srcset for responsive images
      let srcset = variant.image_srcset || productData.featured_image_srcset || "";
      let sizes = "(max-width: 480px) 50vw, (max-width: 749px) 33vw, 25vw";

      // Format variant price
      const price = this.formatMoney(variant.price / 100);
      const comparePrice = variant.compare_at_price > 0 ? this.formatMoney(variant.compare_at_price / 100) : null;

      // Create variant title
      const variantTitle = `${productData.title} - ${variant.title}`;

      // Determine button text and state
      const buttonText = variant.available ? "Add" : "Out of Stock";
      const buttonDisabled = !variant.available;

      variantCard.innerHTML = `
        <!-- Selected Badge -->
        <div class="selected-badge">${productIndex}.${variantIndex}</div>
        
        <!-- Product Image with Loading Placeholder -->
        <div class="product-image-container">
          <div class="product-loading-placeholder"></div>
          <img src="${imageUrl}" 
               ${srcset ? `srcset="${srcset}"` : ""}
               sizes="${sizes}"
               alt="${variantTitle}"
               width="200"
               height="200"
               loading="lazy"
               class="product-image"
               onload="this.style.opacity='1'; this.previousElementSibling.style.display='none';"
               onerror="this.style.opacity='1'; this.previousElementSibling.style.display='none';">
        </div>
        
        <!-- Product Details -->
        <div class="product-name">${variantTitle}</div>
        
        <!-- Product Price -->
        <div class="product-price">
          ${comparePrice ? `<span class="original-price">${comparePrice}</span>` : ""}
          <span class="${comparePrice ? "discount-price" : ""}">${price}</span>
        </div>
        
        <!-- Add Button (Initial State) -->
        <button class="add-to-bundle-btn" data-product-id="${variant.id}" ${buttonDisabled ? "disabled" : ""}>
          ${buttonText}
        </button>
        
        <!-- Quantity Selector (Hidden Initially) -->
        <div class="quantity-selector bundle-builder-hidden">
          <button class="quantity-btn" data-action="decrease">-</button>
          <span class="quantity-display">1</span>
          <button class="quantity-btn" data-action="increase">+</button>
        </div>
        
        <!-- View Details Button 
        <button class="product-details-btn" 
                data-product-url="${productData.url}"
                data-product-id="${variant.id}">
          View Details
        </button> -->
      `;

      return variantCard;
    }

    populateVariantOptions(productData, popup) {
      const variantOptionsContainer = popup.querySelector(".variant-options");
      variantOptionsContainer.innerHTML = "";

      // Get option names from the first variant (they should be consistent)
      const firstVariant = productData.variants[0];
      if (!firstVariant) return;

      // Create option groups for each variant option
      for (let i = 1; i <= 3; i++) {
        const optionKey = `option${i}`;
        const optionValue = firstVariant[optionKey];

        if (optionValue) {
          // Get the option name from the product's option structure
          const optionName = this.getOptionName(productData, i);

          const optionGroup = document.createElement("div");
          optionGroup.className = "variant-option-group";

          const optionLabel = document.createElement("label");
          optionLabel.className = "variant-option-label";
          optionLabel.textContent = optionName;

          const optionSelect = document.createElement("select");
          optionSelect.className = "variant-option-select";
          optionSelect.dataset.optionIndex = i;

          // Add default option
          const defaultOption = document.createElement("option");
          defaultOption.value = "";
          defaultOption.textContent = `Select ${optionName}`;
          defaultOption.disabled = true;
          defaultOption.selected = true;
          optionSelect.appendChild(defaultOption);

          // Get unique values for this option
          const uniqueValues = [...new Set(productData.variants.map((v) => v[optionKey]).filter(Boolean))];

          uniqueValues.forEach((value) => {
            const option = document.createElement("option");
            option.value = value;
            option.textContent = value;

            // Check if this option is available
            const hasAvailableVariant = productData.variants.some((variant) => variant[optionKey] === value && variant.available);

            if (!hasAvailableVariant) {
              option.disabled = true;
              option.textContent += " (Out of Stock)";
            }

            optionSelect.appendChild(option);
          });

          optionSelect.addEventListener("change", () => this.selectVariantOptionFromDropdown(optionSelect));

          optionGroup.appendChild(optionLabel);
          optionGroup.appendChild(optionSelect);
          variantOptionsContainer.appendChild(optionGroup);
        }
      }
    }

    groupVariantsByOptions(variants) {
      const groups = {};

      variants.forEach((variant) => {
        if (variant.option1) {
          if (!groups["Option 1"]) groups["Option 1"] = [];
          groups["Option 1"].push(variant.option1);
        }
        if (variant.option2) {
          if (!groups["Option 2"]) groups["Option 2"] = [];
          groups["Option 2"].push(variant.option2);
        }
        if (variant.option3) {
          if (!groups["Option 3"]) groups["Option 3"] = [];
          groups["Option 3"].push(variant.option3);
        }
      });

      return groups;
    }

    getOptionIndex(optionName) {
      const optionMap = {
        "Option 1": 1,
        "Option 2": 2,
        "Option 3": 3,
      };
      return optionMap[optionName] || 1;
    }

    getOptionName(productData, optionIndex) {
      // Get the option name from the product's options array
      if (productData.options && productData.options[optionIndex - 1]) {
        return productData.options[optionIndex - 1].name;
      }
      // Fallback to generic names
      const optionNames = ["Size", "Color", "Material", "Style"];
      return optionNames[optionIndex - 1] || `Option ${optionIndex}`;
    }

    selectVariantOptionFromDropdown(selectElement) {
      // Find the matching variant based on all selected options
      this.findMatchingVariantFromDropdowns();
    }

    findMatchingVariantFromDropdowns() {
      if (!this.currentVariantProductCard) return;

      const popup = this.currentVariantProductCard.querySelector(".variant-popup-overlay");
      const selects = popup.querySelectorAll(".variant-option-select");

      const selectedOptions = {};
      selects.forEach((select) => {
        const optionIndex = select.dataset.optionIndex;
        if (select.value) {
          selectedOptions[`option${optionIndex}`] = select.value;
        }
      });

      // Find variant that matches all selected options
      const matchingVariant = this.currentVariantProduct.variants.find((variant) => {
        return Object.keys(selectedOptions).every((optionKey) => {
          return variant[optionKey] === selectedOptions[optionKey];
        });
      });

      if (matchingVariant && matchingVariant.available) {
        this.selectedVariant = matchingVariant;
        this.updateVariantAddButton();
      } else {
        this.selectedVariant = null;
        this.updateVariantAddButton();
      }
    }

    selectVariantOption(optionName, optionValue) {
      // Remove selected class from all buttons in this option group
      const optionButtons = document.querySelectorAll(`[data-option-name="${optionName}"]`);
      optionButtons.forEach((btn) => btn.classList.remove("selected"));

      // Add selected class to clicked button
      const selectedButton = document.querySelector(`[data-option-name="${optionName}"][data-option-value="${optionValue}"]`);
      if (selectedButton && !selectedButton.classList.contains("unavailable")) {
        selectedButton.classList.add("selected");
      }

      // Find the matching variant
      this.findMatchingVariant();
    }

    findMatchingVariant() {
      const selectedOptions = {};
      const selectedButtons = document.querySelectorAll(".variant-option-btn.selected");

      selectedButtons.forEach((button) => {
        const optionName = button.dataset.optionName;
        const optionValue = button.dataset.optionValue;
        selectedOptions[optionName] = optionValue;
      });

      // Find variant that matches all selected options
      const matchingVariant = this.currentVariantProduct.variants.find((variant) => {
        return Object.keys(selectedOptions).every((optionName) => {
          const optionIndex = this.getOptionIndex(optionName);
          return variant[`option${optionIndex}`] === selectedOptions[optionName];
        });
      });

      if (matchingVariant && matchingVariant.available) {
        this.selectedVariant = matchingVariant;
        this.updateVariantAddButton();
      } else {
        this.selectedVariant = null;
        this.updateVariantAddButton();
      }
    }

    updateVariantAddButton() {
      // Update the button in the current popup
      if (this.currentVariantProductCard) {
        const popup = this.currentVariantProductCard.querySelector(".variant-popup-overlay");
        const addButton = popup.querySelector(".variant-add-to-bundle-btn");
        if (this.selectedVariant) {
          addButton.disabled = false;
          //addButton.textContent = `Add to Bundle`
          // - ${this.formatMoney(this.selectedVariant.price / 100)}`;
        } else {
          addButton.disabled = true;
          addButton.textContent = "Select Variant";
        }
      }
    }

    closeVariantPopup() {
      // Close all variant popups
      const popups = document.querySelectorAll(".variant-popup-overlay");
      popups.forEach((popup) => {
        popup.classList.remove("visible");
        setTimeout(() => {
          popup.classList.add("bundle-builder-hidden");
        }, 300);
      });

      // Reset variant selection
      this.currentVariantProduct = null;
      this.currentVariantProductCard = null;
      this.selectedVariant = null;
    }

    handleVariantQuantityChange(buttonElement) {
      // Stop event propagation to prevent bubbling to parent handlers
      event.stopPropagation();

      const action = buttonElement.dataset.action;
      const popup = buttonElement.closest(".variant-popup-overlay");
      const quantityDisplay = popup.querySelector(".variant-quantity-display");
      let currentQuantity = parseInt(quantityDisplay.textContent) || 1;

      if (action === "increase") {
        currentQuantity++;
      } else if (action === "decrease" && currentQuantity > 1) {
        currentQuantity--;
      }

      quantityDisplay.textContent = currentQuantity;
    }

    handleVariantAddToBundle() {
      if (!this.selectedVariant || !this.currentVariantProductCard) return;

      // Get quantity from popup
      const popup = this.currentVariantProductCard.querySelector(".variant-popup-overlay");
      const quantityDisplay = popup.querySelector(".variant-quantity-display");
      const quantity = parseInt(quantityDisplay.textContent) || 1;

      // Update product card with variant information
      const productCard = this.currentVariantProductCard;
      const originalProductId = productCard.dataset.originalProductId || productCard.dataset.productId;
      const variantId = this.selectedVariant.id.toString();

      // Store original product ID if not already stored
      if (!productCard.dataset.originalProductId) {
        productCard.dataset.originalProductId = originalProductId;
      }

      // Update product card data attributes
      productCard.dataset.productId = variantId;
      productCard.dataset.productPrice = (this.selectedVariant.price / 100).toString();

      // Update product image if variant has a different image
      if (this.selectedVariant.image) {
        const productImage = productCard.querySelector("img");
        productImage.src = this.selectedVariant.image;
      }

      // Update product name to include variant title
      const productName = productCard.querySelector(".product-name");
      productName.textContent = `${this.currentVariantProduct.title} - ${this.selectedVariant.title}`;

      // Update product price
      const productPrice = productCard.querySelector(".product-price");
      const price = this.formatMoney(this.selectedVariant.price / 100);
      const comparePrice = this.selectedVariant.compare_at_price > 0 ? this.formatMoney(this.selectedVariant.compare_at_price / 100) : null;

      if (comparePrice) {
        productPrice.innerHTML = `<span class="original-price">${comparePrice}</span><span class="discount-price">${price}</span>`;
      } else {
        productPrice.textContent = price;
      }

      // Hide add button and show quantity selector with the selected quantity
      const addButton = productCard.querySelector(".add-to-bundle-btn");
      addButton.classList.add("bundle-builder-hidden");
      const quantitySelector = productCard.querySelector(".quantity-selector");
      quantitySelector.classList.remove("bundle-builder-hidden");
      const cardQuantityDisplay = quantitySelector.querySelector(".quantity-display");
      cardQuantityDisplay.textContent = quantity.toString();

      // Add product to selection and update UI
      productCard.classList.add("selected");
      this.addProductToSelection(productCard, quantity);

      // Update bundle preview and progress
      this.updateBundlePreview();
      this.updateProgress();
      this.updateBundleSummary();

      // Trigger mobile cart animation
      this.animateCartSlide();

      // Update mobile cart visibility
      this.updateMobileCartVisibility();

      // Close popup
      this.closeVariantPopup();
    }

    handleQuantityChange(buttonElement) {
      // Stop event propagation to prevent bubbling to parent handlers
      event.stopPropagation();

      const action = buttonElement.dataset.action;
      const productCard = buttonElement.closest(".product-card");
      const quantityDisplay = productCard.querySelector(".quantity-display");
      let currentQuantity = parseInt(quantityDisplay.textContent) || 1;

      if (action === "increase") {
        currentQuantity++;
      } else if (action === "decrease" && currentQuantity > 1) {
        currentQuantity--;
      }
      // Prevent going below 1 - minimum quantity is 1

      quantityDisplay.textContent = currentQuantity;

      // No longer add/remove items from bundle here
      // Quantity selector only controls the quantity for the next "Add to Bundle" action
    }

    addProductToSelection(productCard, quantity) {
      const productId = productCard.dataset.productId;

      // Get product details
      const productName = productCard.querySelector(".product-name").textContent;
      const productImage = productCard.querySelector("img").src;
      const priceElement = productCard.querySelector(".product-price");

      // Extract price - handle both static and AJAX-loaded products
      let price = this.extractPrice(priceElement);

      // If price extraction failed, try to get from data attributes
      if (price === 0 && productCard.dataset.productPrice) {
        price = parseFloat(productCard.dataset.productPrice);
      }

      // Determine variant ID based on inline variant selection
      let variantId = productId;
      if (productCard.dataset.hasVariants === "true") {
        // Product has variants - use selected variant ID
        variantId = productCard.dataset.selectedVariantId || productCard.dataset.defaultVariantId || productId;
      } else if (productCard.dataset.hasVariants === "false" && productCard.dataset.defaultVariantId) {
        // Product has no variants but has default variant ID
        variantId = productCard.dataset.defaultVariantId;
      }

      // Add multiple line items based on the quantity selector
      // Each item gets a unique timestamp to ensure uniqueness
      for (let i = 0; i < quantity; i++) {
        const timestamp = Date.now() + i; // Ensure unique timestamps
        const uniqueKey = `${productId}_${variantId}_${timestamp}`;

        this.selectedProducts.set(uniqueKey, {
          id: variantId,
          handle: productCard.dataset.productHandle,
          quantity: 1, // Always 1 for individual line items
          name: productName,
          price: price,
          image: productImage,
          productId: productId, // Store original product ID for reference
          variantId: variantId, // Store variant ID for reference
          timestamp: timestamp, // Store timestamp for uniqueness
        });
      }
    }

    selectDeliveryOption(optionElement) {
      // Remove selected class from all options
      document.querySelectorAll(".delivery-option").forEach((option) => {
        option.classList.remove("selected");
      });

      // Add selected class to clicked option
      optionElement.classList.add("selected");

      // Update delivery type
      this.deliveryType = optionElement.dataset.deliveryType;

      this.updateBundleSummary();
    }

    openProductDetails(buttonElement) {
      const productUrl = buttonElement.dataset.productUrl;
      if (productUrl) {
        window.open(productUrl, "_blank");
      }
    }

    removeFromBundle(removeButton) {
      const bundleItem = removeButton.closest(".bundle-item");
      const uniqueKey = bundleItem.dataset.productId; // This is now the unique key with timestamp

      // Remove from selected products
      this.selectedProducts.delete(uniqueKey);

      // Extract product ID from unique key for finding the product card
      let productId;
      if (uniqueKey.includes("_")) {
        // Unique key contains product ID, variant ID, and timestamp (e.g., "123_456_1234567890")
        productId = uniqueKey.split("_")[0];
      } else {
        // Unique key is just the product ID (no variants)
        productId = uniqueKey;
      }

      // Reset product card to initial state
      const productCard = document.querySelector(`[data-product-id="${productId}"]`);
      if (productCard) {
        // Reset quantity to 1 (minimum) for the next "Add to Bundle" action
        const quantityDisplay = productCard.querySelector(".quantity-display");
        if (quantityDisplay) {
          quantityDisplay.textContent = "1";
        }

        // Reset product card to default state if it has variants
        if (productCard.dataset.hasVariants === "true") {
          this.resetProductCardToDefault(productCard);

          // Reset variant dropdown to default
          const variantDropdown = productCard.querySelector(".variant-dropdown");
          if (variantDropdown) {
            variantDropdown.value = "";
          }
        }
      }

      // Remove bundle item
      bundleItem.remove();

      this.updateProgress();
      this.updateBundleSummary();

      // Update mobile cart visibility
      this.updateMobileCartVisibility();
    }

    async handleCheckout(buttonElement) {
      if (this.selectedProducts.size === 0) {
        alert("Please select at least one product to continue.");
        return;
      }

      if (!this.currentTier) {
        alert("Please select a bundle tier to continue.");
        return;
      }

      // Show checkout loading overlay
      this.showCheckoutLoading();

      try {
        // Build cart items array
        const cartItems = Array.from(this.selectedProducts.values()).map((product) => ({
          id: product.id,
          quantity: product.quantity,
        }));

        // Add delivery type to cart
        const cartData = {
          items: cartItems,
          attributes: {
            "Bundle Tier": this.currentTier.id,
            "Delivery Type": this.deliveryType,
          },
        };

        // Add to cart using Shopify AJAX API
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(cartData),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || "Failed to add items to cart");
        }

        // Redirect to cart or checkout
        if (window.redirectToCart) {
          window.location.href = "/cart";
        } else {
          window.location.href = "/checkout";
        }
      } catch (error) {
        // Hide loading overlay on error
        this.hideCheckoutLoading();
        console.error("Checkout error:", error);
        alert("Error processing checkout: " + error.message);
      }
    }

    preloadCollectionImages(collectionHandle) {
      // Get collection data from pre-loaded JSON
      const collectionData = this.collectionsData.collections[collectionHandle];

      if (!collectionData || !collectionData.products) return;

      // Preload images for all products in the collection
      collectionData.products.forEach((product) => {
        // Preload featured image
        if (product.featured_image) {
          this.preloadImage(product.featured_image);
        }

        // Preload variant images
        if (product.variants) {
          product.variants.forEach((variant) => {
            if (variant.image) {
              this.preloadImage(variant.image);
            }
          });
        }
      });
    }

    loadCollectionProductsWithPlaceholders(collectionHandle) {
      const productGrid = document.querySelector(".product-grid");
      if (!productGrid) return;

      // Get collection data from pre-loaded JSON
      const collectionData = this.collectionsData.collections[collectionHandle];

      if (!collectionData || !collectionData.products || collectionData.products.length === 0) {
        productGrid.innerHTML = '<div class="product-grid-placeholder"><p>No products found in this collection</p></div>';
        return;
      }

      // Clear existing products and render new ones with loading placeholders
      productGrid.innerHTML = "";
      console.log("Collection Data", collectionData);

      // Render products from the JSON data
      collectionData.products.forEach((product, index) => {
        if (this.variantHandling === "cards" && product.has_variants === true) {
          // Create individual cards for each variant
          let hasVisibleVariants = false;
          product.variants.forEach((variant, variantIndex) => {
            // Only show variant if it's available OR if showOutOfStock is enabled
            const shouldShowVariant = variant.available || this.showOutOfStock;
            if (shouldShowVariant) {
              const variantCard = this.createVariantCardWithPlaceholder(variant, product, index + 1, variantIndex + 1);
              productGrid.appendChild(variantCard);
              hasVisibleVariants = true;
            }
          });

          // If no variants are visible, don't show anything for this product
          if (!hasVisibleVariants) {
            // Skip this product entirely
            return;
          }
        } else {
          // Popup mode or no variants - check if product should be shown
          if (product.has_variants === true) {
            // Product has variants but we're in popup mode
            // Check if any variants are available OR if showOutOfStock is enabled
            const hasAvailableVariants = product.variants.some((variant) => variant.available);
            const shouldShowProduct = hasAvailableVariants || this.showOutOfStock;

            if (shouldShowProduct) {
              const isOutOfStock = !hasAvailableVariants && this.showOutOfStock;
              const productCard = this.createProductCardWithPlaceholder(product, index + 1, collectionData.id, isOutOfStock);
              productGrid.appendChild(productCard);
            }
          } else {
            // No variants or single variant product - check availability
            if (product.variants && product.variants.length === 1) {
              // Single variant product
              const variant = product.variants[0];
              const shouldShowProduct = variant.available || this.showOutOfStock;

              if (shouldShowProduct) {
                const isOutOfStock = !variant.available && this.showOutOfStock;
                const productCard = this.createProductCardWithPlaceholder(product, index + 1, collectionData.id, isOutOfStock);
                productGrid.appendChild(productCard);
              }
            } else {
              // No variants - always show
              const productCard = this.createProductCardWithPlaceholder(product, index + 1, collectionData.id, false);
              productGrid.appendChild(productCard);
            }
          }
        }
      });
    }

    loadCollectionProducts(collectionHandle) {
      const productGrid = document.querySelector(".product-grid");
      if (!productGrid) return;

      // Get collection data from pre-loaded JSON
      const collectionData = this.collectionsData.collections[collectionHandle];

      if (!collectionData || !collectionData.products || collectionData.products.length === 0) {
        productGrid.innerHTML = '<div class="product-grid-placeholder"><p>No products found in this collection</p></div>';
        return;
      }

      // Clear existing products and render new ones
      productGrid.innerHTML = "";
      console.log("Collection Data", collectionData);
      // Render products from the JSON data
      collectionData.products.forEach((product, index) => {
        if (this.variantHandling === "cards" && product.has_variants === true) {
          // Create individual cards for each variant
          let hasVisibleVariants = false;
          product.variants.forEach((variant, variantIndex) => {
            // Only show variant if it's available OR if showOutOfStock is enabled
            const shouldShowVariant = variant.available || this.showOutOfStock;
            if (shouldShowVariant) {
              const variantCard = this.createVariantCard(variant, product, index + 1, variantIndex + 1);
              productGrid.appendChild(variantCard);
              hasVisibleVariants = true;
            }
          });

          // If no variants are visible, don't show anything for this product
          if (!hasVisibleVariants) {
            // Skip this product entirely
            return;
          }
        } else {
          // Popup mode or no variants - check if product should be shown
          if (product.has_variants === true) {
            // Product has variants but we're in popup mode
            // Check if any variants are available OR if showOutOfStock is enabled
            const hasAvailableVariants = product.variants.some((variant) => variant.available);
            const shouldShowProduct = hasAvailableVariants || this.showOutOfStock;

            if (shouldShowProduct) {
              const isOutOfStock = !hasAvailableVariants && this.showOutOfStock;
              const productCard = this.createProductCard(product, index + 1, collectionData.id, isOutOfStock);
              productGrid.appendChild(productCard);
            }
          } else {
            // No variants or single variant product - check availability
            if (product.variants && product.variants.length === 1) {
              // Single variant product
              const variant = product.variants[0];
              const shouldShowProduct = variant.available || this.showOutOfStock;

              if (shouldShowProduct) {
                const isOutOfStock = !variant.available && this.showOutOfStock;
                const productCard = this.createProductCard(product, index + 1, collectionData.id, isOutOfStock);
                productGrid.appendChild(productCard);
              }
            } else {
              // No variants - always show
              const productCard = this.createProductCard(product, index + 1, collectionData.id, false);
              productGrid.appendChild(productCard);
            }
          }
        }
      });
    }

    createVariantDropdown(product) {
      // If product has no variants or only default variant, show disabled dropdown
      if (!product.has_variants || product.variants.length <= 1) {
        return `
        <label class="variant-label">Color</label>
        <select class="variant-dropdown" disabled>
          <option value="">Default - No variant</option>
        </select>
      `;
      }

      // Create dropdown for products with variants
      let options = '<option value="">Select Color</option>';

      product.variants.forEach((variant) => {
        const isAvailable = variant.available || this.showOutOfStock;
        const disabled = !isAvailable ? "disabled" : "";
        const outOfStockText = !variant.available ? " (Out of Stock)" : "";

        options += `
        <option value="${variant.id}" ${disabled}>
          ${variant.title}${outOfStockText}
        </option>
      `;
      });

      return `
      <label class="variant-label">Color</label>
      <select class="variant-dropdown">
        ${options}
      </select>
    `;
    }

    createProductCard(product, index, collectionId, isOutOfStock = false) {
      const productCard = document.createElement("div");
      productCard.className = "product-card";
      productCard.dataset.productId = product.id;
      productCard.dataset.productHandle = product.handle;
      productCard.dataset.collectionId = collectionId;
      productCard.dataset.productPrice = (product.price / 100).toString(); // Store price in dollars
      productCard.dataset.hasVariants = product.has_variants === true ? "true" : "false";
      productCard.dataset.defaultVariantId = product.default_variant_id;

      // Add out-of-stock class if needed
      if (isOutOfStock) {
        productCard.classList.add("out-of-stock");
      }

      // Get product image with responsive srcset
      const imageUrl =
        product.featured_image ||
        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23f5f5f5"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial, sans-serif" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';

      // Create srcset for responsive images
      let srcset = product.featured_image_srcset || "";
      let sizes = "(max-width: 480px) 50vw, (max-width: 749px) 33vw, 25vw";

      // Format price
      console.log("product", product)
      
      
      const price = this.formatMoney(product.price / 100); // Shopify prices are in cents
      const comparePrice = product.compare_at_price > 0 ? this.formatMoney(product.compare_at_price / 100) : null;

      // Get product description
      const description = product.description || `${product.title} - Premium quality product`;

      // Determine button text and state
      const buttonText = isOutOfStock ? "Out of Stock" : "Add to Bundle";
      const buttonDisabled = isOutOfStock;

      // Create variant dropdown HTML
      const variantDropdown = this.createVariantDropdown(product);

      productCard.innerHTML = `
       <!-- Selected Badge -->
       <div class="selected-badge">${index}</div>
       
       <!-- Product Image -->
       <div class="product-image-container">
       <img src="${imageUrl}" 
              ${srcset ? `srcset="${srcset}"` : ""}
              sizes="${sizes}"
            alt="${product.title}"
            width="200"
            height="200"
              loading="lazy"
              class="product-image">
       </div>
       
       <!-- Product Details -->
       <div class="product-name">${product.title}</div>
      <!--  <div class="product-description">${description}</div> -->
       
       <!-- Product Price -->
       <div class="product-price">
         ${comparePrice ? `<span class="original-price">${comparePrice}</span>` : ""}
         <span class="${comparePrice ? "discount-price" : ""}">${price}</span>
        <!--  ${product.price_varies ? " and up" : ""} -->
       </div>
       
       <!-- Variant Selection Dropdown (Always Visible) -->
       <div class="variant-selector">
         ${variantDropdown}
       </div>
       
       <!-- Quantity Selector (Always Visible) -->
       <div class="quantity-selector">
         <button class="quantity-btn" data-action="decrease">-</button>
         <span class="quantity-display">1</span>
         <button class="quantity-btn" data-action="increase">+</button>
       </div>
       
       <!-- Add Button (Always Visible) -->
       <button class="add-to-bundle-btn" data-product-id="${product.id}" ${buttonDisabled ? "disabled" : ""}>
         ${buttonText}
       </button>
       
       <!-- View Details Button 
        <button class="product-details-btn" 
                data-product-url="${product.url}"
                data-product-id="${product.id}">
          View Details
        </button> -->
      `;

      return productCard;
    }

    createProductCardWithPlaceholder(product, index, collectionId, isOutOfStock = false) {
      const productCard = document.createElement("div");
      productCard.className = "product-card";
      productCard.dataset.productId = product.id;
      productCard.dataset.productHandle = product.handle;
      productCard.dataset.collectionId = collectionId;
      productCard.dataset.productPrice = (product.price / 100).toString(); // Store price in dollars
      productCard.dataset.hasVariants = product.has_variants === true ? "true" : "false";
      productCard.dataset.defaultVariantId = product.default_variant_id;

      // Add out-of-stock class if needed
      if (isOutOfStock) {
        productCard.classList.add("out-of-stock");
      }

      // Get product image with responsive srcset
      const imageUrl =
        product.featured_image ||
        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23f5f5f5"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial, sans-serif" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';

      // Create srcset for responsive images
      let srcset = product.featured_image_srcset || "";
      let sizes = "(max-width: 480px) 50vw, (max-width: 749px) 33vw, 25vw";

      // Format price
      const price = this.formatMoney((parseFloat(product.price).toFixed()) / 100); // Shopify prices are in cents
      const comparePrice = product.compare_at_price > 0 ? this.formatMoney(product.compare_at_price / 100) : null;

      // Get product description
      const description = product.description || `${product.title} - Premium quality product`;

      // Determine button text and state
      const buttonText = isOutOfStock ? "Out of Stock" : "Add to Bundle";
      const buttonDisabled = isOutOfStock;

      // Create variant dropdown HTML
      const variantDropdown = this.createVariantDropdown(product);

      productCard.innerHTML = `
       <!-- Selected Badge -->
       <div class="selected-badge">${index}</div>
       
       <!-- Product Image with Loading Placeholder -->
       <div class="product-image-container">
         <div class="product-loading-placeholder"></div>
         <img src="${imageUrl}" 
              ${srcset ? `srcset="${srcset}"` : ""}
              sizes="${sizes}"
              alt="${product.title}"
              width="200"
              height="200"
              loading="lazy"
              class="product-image"
              onload="this.style.opacity='1'; this.previousElementSibling.style.display='none';"
              onerror="this.style.opacity='1'; this.previousElementSibling.style.display='none';">
       </div>
       
       <!-- Product Details -->
       <div class="product-name">${product.title}</div>
      <!--  <div class="product-description">${description}</div> -->
       
       <!-- Product Price -->
       <div class="product-price">
         ${comparePrice ? `<span class="original-price">${comparePrice}</span>` : ""}
         <span class="${comparePrice ? "discount-price" : ""}">${price}</span>
         <!-- ${product.price_varies ? " and up" : ""} -->
       </div>
       
       <!-- Variant Selection Dropdown (Always Visible) -->
       <div class="variant-selector">
         ${variantDropdown}
       </div>
       
       <!-- Quantity Selector (Always Visible) -->
       <div class="quantity-selector">
         <button class="quantity-btn" data-action="decrease">-</button>
         <span class="quantity-display">1</span>
         <button class="quantity-btn" data-action="increase">+</button>
       </div>
       
       <!-- Add Button (Always Visible) -->
       <button class="add-to-bundle-btn" data-product-id="${product.id}" ${buttonDisabled ? "disabled" : ""}>
         ${buttonText}
       </button>
       
       <!-- View Details Button 
        <button class="product-details-btn" 
                data-product-url="${product.url}"
                data-product-id="${product.id}">
          View Details
        </button> -->
      `;

      return productCard;
    }

    updateBundlePreview() {
      const bundleItemsList = document.querySelector(".bundle-items-list");
      if (!bundleItemsList) return;

      // Clear existing items
      bundleItemsList.innerHTML = "";

      if (this.selectedProducts.size === 0) {
        bundleItemsList.innerHTML = '<div class="empty-bundle-message"><p>No items selected yet. Choose products from the left to build your bundle!</p></div>';
        this.updateScrollIndicator();
        return;
      }

      // Add each selected product
      this.selectedProducts.forEach((product, uniqueKey) => {
        const bundleItem = document.createElement("div");
        bundleItem.className = "bundle-item";
        bundleItem.dataset.productId = uniqueKey; // Store the unique key

        // Different layout for mobile vs desktop
        if (this.isMobile) {
          // Check if we're in full view mode
          const isFullView = document.querySelector(".bundle-builder-right-pane")?.classList.contains("full-view");

          if (isFullView) {
            // Full view mode - show complete desktop layout
            bundleItem.innerHTML = `
             <div class="bundle-item-image-container">
               <div class="bundle-item-loading-placeholder"></div>
               <img src="${product.image}" 
                    srcset="${product.image.replace(/width=\d+/, "width=48")} 48w, ${product.image.replace(/width=\d+/, "width=96")} 96w"
                    sizes="48px"
                    alt="${product.name}"
                    class="bundle-item-image"
                    onload="this.style.opacity='1'; this.previousElementSibling.style.display='none';"
                    onerror="this.style.opacity='1'; this.previousElementSibling.style.display='none';">
             </div>
            <div class="bundle-item-details">
              <div class="bundle-item-name">${product.name}</div>
              <!--<div class="bundle-item-flavor">${product.name}</div>-->
            </div>
            <div class="remove-item">✕</div>
          `;
            bundleItemsList.style.flexDirection = "column";
          } else {
            // Compact view mode - show only images in horizontal scroll
            bundleItem.innerHTML = `
             <div class="bundle-item-image-container">
               <div class="bundle-item-loading-placeholder"></div>
               <img src="${product.image}" 
                    srcset="${product.image.replace(/width=\d+/, "width=60")} 60w, ${product.image.replace(/width=\d+/, "width=120")} 120w"
                    sizes="60px"
                    alt="${product.name}" 
                    class="bundle-item-image mobile-bundle-item-image"
                    onload="this.style.opacity='1'; this.previousElementSibling.style.display='none';"
                    onerror="this.style.opacity='1'; this.previousElementSibling.style.display='none';">
             </div>
          `;
            bundleItemsList.style.flexDirection = "row";
          }
        } else {
          // Desktop layout
          bundleItem.innerHTML = `
           <div class="bundle-item-image-container">
             <div class="bundle-item-loading-placeholder"></div>
             <img src="${product.image}" 
                  srcset="${product.image.replace(/width=\d+/, "width=60")} 60w, ${product.image.replace(/width=\d+/, "width=120")} 120w"
                  sizes="60px"
                  alt="${product.name}"
                  class="bundle-item-image"
                  onload="this.style.opacity='1'; this.previousElementSibling.style.display='none';"
                  onerror="this.style.opacity='1'; this.previousElementSibling.style.display='none';">
           </div>
          <div class="bundle-item-details">
            <div class="bundle-item-name">${product.name}</div>
            <!--<div class="bundle-item-flavor">${product.name}</div>-->
          </div>
          <div class="remove-item">✕</div>
        `;
        }

        bundleItemsList.appendChild(bundleItem);
        // After adding bundle items in updateBundlePreview()
        //     requestAnimationFrame(() => {
        //   if (!this.isMobile) {
        //     const bundleItems = document.querySelector('.bundle-items');
        //     const scrollIndicator = document.querySelector('.scroll-indicator');

        //     // Check if scroll indicator is visible (meaning there's content below)
        //     if (scrollIndicator && scrollIndicator.style.opacity === "1") {
        //       debugger;
        //       // Check if user is at bottom
        //       const isAtBottom = bundleItems.scrollTop + bundleItems.clientHeight >= bundleItems.scrollHeight - 1;

        //       if (isAtBottom) {
        //         // Auto-scroll to show new item
        //         bundleItems.scrollTo({
        //           top: bundleItems.scrollHeight,
        //           behavior: 'smooth'
        //         });
        //       }
        //     }
        //   }
        // });
      });

      // Update scroll indicator after adding items
      this.updateScrollIndicator();

      // Update mobile cart visibility
      this.updateMobileCartVisibility();
    }

    updateProgress() {
      // Calculate total quantity of all selected products
      let totalSelectedQuantity = 0;
      this.selectedProducts.forEach((product) => {
        totalSelectedQuantity += product.quantity;
      });

      // Auto-switch tier if user has exceeded current tier limit
      if (this.currentTier && totalSelectedQuantity > this.currentTier.quantity) {
        this.autoSwitchTier(totalSelectedQuantity);
      }

      const totalCount = this.currentTier ? this.currentTier.quantity : 0;

      const selectedCountElement = document.querySelector(".selected-count");
      const totalCountElement = document.querySelector(".total-count");
      const progressFill = document.querySelector(".progress-fill");
      const progressText = document.querySelector(".progress-text");

      if (selectedCountElement) selectedCountElement.textContent = totalSelectedQuantity;
      if (totalCountElement) totalCountElement.textContent = totalCount;

      // Update progress text to show correct format
      if (progressText && totalCount > 0) {
        progressText.innerHTML = `You've selected <span class="selected-count">${totalSelectedQuantity}</span> of <span class="total-count">${totalCount}</span> items`;
      } else if (progressText) {
        progressText.innerHTML = `You've selected <span class="selected-count">${totalSelectedQuantity}</span> of <span class="total-count">0</span> items`;
      }

      if (progressFill && totalCount > 0) {
        const progressPercentage = Math.min((totalSelectedQuantity / totalCount) * 100, 100);
        progressFill.style.width = `${progressPercentage}%`;
      } else if (progressFill) {
        progressFill.style.width = "0%";
      }

      const bundleCheckoutContainer = document.querySelector(".bundle-checkout");
      if (totalSelectedQuantity > 0 && totalSelectedQuantity >= totalCount) {
        //progressFill.style.width = '100%';
        // bundleCheckoutContainer.style.display = 'block';
        // bundleCheckoutContainer.innerHTML = 'Review Cart';
        //this.addMobileCartExpandButton()
        this.updateMobileLayout();
      }

      // Update checkout button visibility based on progress
      this.updateBundleSummary();

      // Removed automatic cart expansion toggle to prevent unwanted behavior when selecting tiers
    }

    updateBundleSummary() {
      let subtotal = 0;

      // Calculate subtotal
      this.selectedProducts.forEach((product) => {
        subtotal += product.price * product.quantity;
      });

      // Calculate discount (20% off for bundles)
      const discount = subtotal * 0.2;
      const finalTotal = subtotal - discount;

      // Update summary elements
      const subtotalElement = document.querySelector(".subtotal-amount");
      const discountElement = document.querySelector(".discount-amount");
      const totalElement = document.querySelector(".total-amount");
      const checkoutButton = document.querySelector(".checkout-button");

      if (subtotalElement) subtotalElement.textContent = this.formatMoney(subtotal);
      if (discountElement) discountElement.textContent = `-${this.formatMoney(discount)}`;
      if (totalElement) totalElement.textContent = this.formatMoney(finalTotal);

      // Calculate total quantity of selected products
      let totalSelectedQuantity = 0;
      this.selectedProducts.forEach((product) => {
        totalSelectedQuantity += product.quantity;
      });

      // Show/hide checkout button based on tier requirements
      if (checkoutButton) {
        const hasProducts = this.selectedProducts.size > 0;
        const hasTier = this.currentTier !== null;
        const meetsTierRequirement = hasTier && totalSelectedQuantity >= this.currentTier.quantity;

        // Show button only when all conditions are met
        if (hasProducts && hasTier && meetsTierRequirement) {
          checkoutButton.style.display = "block";
          checkoutButton.disabled = false;
        } else {
          checkoutButton.style.display = "none";
          checkoutButton.disabled = true;
        }
      }
    }

    extractPrice(priceElement) {
      // Extract numeric price from price element
      const priceText = priceElement.textContent;
      // Handle both formatted prices ($XX.XX) and numeric values
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      return priceMatch ? parseFloat(priceMatch[0].replace(",", "")) : 0;
    }

    // Helper method to get product price in cents for AJAX-loaded products
    getProductPriceInCents(product) {
      return product.price || 0;
    }

    formatMoney(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
    }

    autoSwitchTier(requiredQuantity) {
      // Get all available tiers
      const availableTiers = this.getAvailableTiers();

      // Find the smallest tier that can accommodate the required quantity
      const appropriateTier = availableTiers.find((tier) => tier.quantity >= requiredQuantity);

      if (appropriateTier && appropriateTier.id !== this.currentTier.id) {
        // Switch to the appropriate tier
        this.switchToTier(appropriateTier.id);
      }
    }

    getAvailableTiers() {
      const tierElements = document.querySelectorAll(".bundle-tier-option");
      const tiers = [];

      tierElements.forEach((element) => {
        tiers.push({
          id: element.dataset.tierId,
          quantity: parseInt(element.dataset.tierQuantity),
          element: element,
        });
      });

      // Sort tiers by quantity (ascending)
      return tiers.sort((a, b) => a.quantity - b.quantity);
    }

    switchToTier(tierId) {
      // Find the tier element
      const tierElement = document.querySelector(`[data-tier-id="${tierId}"]`);

      if (tierElement) {
        // Update current tier
        this.currentTier = {
          id: tierId,
          quantity: parseInt(tierElement.dataset.tierQuantity),
        };

        // Update UI selection
        this.updateTierSelectionUI(tierId);

        // Update progress and checkout button visibility
        this.updateProgress();
      }
    }

    updateTierSelectionUI(selectedTierId) {
      // Remove selected class from all tiers
      document.querySelectorAll(".bundle-tier-option").forEach((tier) => {
        tier.classList.remove("selected");
      });

      // Add selected class to the new tier
      const selectedTierElement = document.querySelector(`[data-tier-id="${selectedTierId}"]`);
      if (selectedTierElement) {
        selectedTierElement.classList.add("selected");
      }
    }

    showTierSelectionError() {
      // Create or show error message
      let errorMessage = document.querySelector(".tier-selection-error");
      if (!errorMessage) {
        errorMessage = document.createElement("div");
        errorMessage.className = "tier-selection-error";
        errorMessage.innerHTML = `
        <div class="error-message">
          <span class="error-icon">⚠️</span>
          <span class="error-text">Please select a bundle size first before adding products.</span>
        </div>
      `;

        // Insert after the bundle tiers section
        const tiersSection = document.querySelector(".bundle-tiers-section");
        if (tiersSection) {
          tiersSection.parentNode.insertBefore(errorMessage, tiersSection.nextSibling);
        }
      }

      // Show the error message
      errorMessage.style.display = "block";

      // Auto-hide after 5 seconds
      setTimeout(() => {
        errorMessage.style.display = "none";
      }, 5000);

      // Also scroll to the tiers section to draw attention
      const tiersSection = document.querySelector(".bundle-tiers-section");
      if (tiersSection) {
        tiersSection.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    updateScrollIndicator() {
      const bundleItems = document.querySelector(".bundle-items");
      const scrollIndicator = document.querySelector(".scroll-indicator");

      if (!bundleItems || !scrollIndicator) return;

      // Check if content is scrollable
      const isScrollable = bundleItems.scrollHeight > bundleItems.clientHeight;
      if (!isScrollable) {
        // Not scrollable, hide indicator
        scrollIndicator.style.opacity = "0";
        return;
      }

      // Check scroll position
      const scrollTop = bundleItems.scrollTop;
      const scrollHeight = bundleItems.scrollHeight;
      const clientHeight = bundleItems.clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1; // 1px tolerance
      const isAtTop = scrollTop <= 1; // 1px tolerance

      if (isAtBottom) {
        // At bottom, hide indicator
        scrollIndicator.style.opacity = "0";
      } else if (isAtTop) {
        // At top, show indicator
        scrollIndicator.style.opacity = "1";
      } else {
        // In middle, hide indicator
        scrollIndicator.style.opacity = "0";
      }
    }

    // Mobile-specific methods
    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 1024;

      // If mobile state changed, update layout
      if (wasMobile !== this.isMobile) {
        this.updateMobileLayout();
      }
    }

    updateMobileLayout() {
      if (this.isMobile) {
        // Add mobile-specific classes
        document.body.classList.add("bundle-builder-mobile");

        // Check if cart should be visible based on selected products
        this.updateMobileCartVisibility();

        // Add mobile cart expand button if not exists
        this.addMobileCartExpandButton();
      } else {
        // Remove mobile-specific classes
        document.body.classList.remove("bundle-builder-mobile");

        // Remove mobile cart expand button
        const expandButton = document.querySelector(".mobile-cart-expand");
        if (expandButton) {
          expandButton.remove();
        }
      }
    }

    updateMobileCartVisibility() {
      if (!this.isMobile) return;

      const rightPane = document.querySelector(".bundle-builder-right-pane");
      if (!rightPane) return;

      const hasProducts = this.selectedProducts.size > 0;

      if (hasProducts) {
        // Show cart with animation
        rightPane.classList.add("cart-visible");
      } else {
        // Hide cart with animation
        rightPane.classList.remove("cart-visible");
      }
      // Update cart preview height after cart visibility changes
      this.updateCartPreviewHeight();
    }

    addMobileCartExpandButton() {
      if (!this.isMobile) return;

      let expandButton = document.querySelector(".mobile-cart-expand");
      if (!expandButton) {
        expandButton = document.createElement("div");
        expandButton.className = "mobile-cart-expand";
        expandButton.innerHTML = `
        <span class="expand-text">Review Cart</span>
      `;

        // Insert after bundle items
        const bundleItems = document.querySelector(".bundle-summary");
        if (bundleItems) {
          bundleItems.parentNode.insertBefore(expandButton, bundleItems.nextSibling);
        }
        // const bundleItemListMobile = document.querySelector('.bundle-items-list');
        // bundleItemListMobile.style.flexDirection = 'column';
        this.updateBundlePreview();
      }

      // Ensure the button is visible and properly positioned
      expandButton.style.display = "flex";

      // Also ensure the button is added to the bundle preview container
      const bundlePreview = document.querySelector(".bundle-preview");
      if (bundlePreview && !bundlePreview.querySelector(".mobile-cart-expand")) {
        bundlePreview.appendChild(expandButton);
      }
    }

    toggleMobileCartExpansion() {
      const rightPane = document.querySelector(".bundle-builder-right-pane");
      const expandButton = document.querySelector(".mobile-cart-expand");

      if (!rightPane || !expandButton) return;

      const isFullView = rightPane.classList.contains("full-view");

      if (isFullView) {
        // Switch back to compact view
        rightPane.classList.remove("full-view");
        expandButton.classList.remove("full-view");
        expandButton.querySelector(".expand-text").textContent = "Review Cart";

        // Update bundle preview to show compact layout
        this.updateBundlePreview();
      } else {
        // Switch to full view
        rightPane.classList.add("full-view");
        expandButton.classList.add("full-view");
        expandButton.querySelector(".expand-text").textContent = "Checkout";

        // Add close button for full view mode
        this.addMobileCartCloseButton();

        // Update bundle preview to show full layout
        this.updateBundlePreview();
      }
    }

    addMobileCartCloseButton() {
      if (!this.isMobile) return;

      let closeButton = document.querySelector(".mobile-cart-close");
      if (!closeButton) {
        closeButton = document.createElement("div");
        closeButton.className = "mobile-cart-close";
        closeButton.innerHTML = `
        <span class="close-icon">✕</span>
      `;

        // Insert at the top of the bundle preview
        const bundlePreview = document.querySelector(".bundle-preview");
        if (bundlePreview) {
          bundlePreview.insertBefore(closeButton, bundlePreview.firstChild);
        }
      }

      // Ensure the button is visible
      closeButton.style.display = "flex";
    }

    closeMobileCartFullView() {
      const rightPane = document.querySelector(".bundle-builder-right-pane");
      const expandButton = document.querySelector(".mobile-cart-expand");
      const closeButton = document.querySelector(".mobile-cart-close");

      if (!rightPane || !expandButton) return;

      // Switch back to compact view
      rightPane.classList.remove("full-view");
      expandButton.classList.remove("full-view");
      expandButton.querySelector(".expand-text").textContent = "Review Cart";

      // Hide close button
      if (closeButton) {
        closeButton.style.display = "none";
      }

      // Update bundle preview to show compact layout
      this.updateBundlePreview();
    }

    // Preload product images for better UX
    preloadProductImages(productCard) {
      const productId = productCard.dataset.productId;
      const productData = this.getProductData(productId);

      if (!productData) return;

      // Create a Set to track preloaded images and avoid duplicates
      if (!this.preloadedImages) {
        this.preloadedImages = new Set();
      }

      // Preload featured image
      if (productData.featured_image && !this.preloadedImages.has(productData.featured_image)) {
        this.preloadImage(productData.featured_image);
        this.preloadedImages.add(productData.featured_image);
      }

      // Preload variant images if product has variants
      if (productData.has_variants && productData.variants) {
        productData.variants.forEach((variant) => {
          if (variant.image && !this.preloadedImages.has(variant.image)) {
            this.preloadImage(variant.image);
            this.preloadedImages.add(variant.image);
          }
        });
      }
    }

    // Helper method to preload a single image
    preloadImage(src) {
      const img = new Image();
      img.src = src;
      // Optional: Add error handling
      img.onerror = () => {
        console.warn("Failed to preload image:", src);
      };
    }

    // Mobile cart sliding animation
    animateCartSlide() {
      if (!this.isMobile) return;

      const bundleItems = document.querySelector(".bundle-items-list");
      if (!bundleItems) return;

      // Add sliding animation class
      bundleItems.classList.add("cart-slide-animation");

      // Remove animation class after animation completes
      setTimeout(() => {
        bundleItems.classList.remove("cart-slide-animation");
      }, 300);
    }

    // Infinite Scroll Implementation
    initInfiniteScroll() {
      this.currentPage = 1;
      this.productsPerPage = 20;
      this.isLoading = false;
      this.hasMoreProducts = true;
      this.scrollThrottle = null;

      // Add throttled scroll event listener
      //window.addEventListener('scroll', this.handleScrollThrottled.bind(this));

      // Initialize with current collection data
      this.updateTotalProducts();
    }

    handleScrollThrottled() {
      if (this.scrollThrottle) return;

      this.scrollThrottle = setTimeout(() => {
        this.handleScroll();
        this.scrollThrottle = null;
      }, 100); // Throttle to 100ms
    }

    handleScroll() {
      if (this.isLoading || !this.hasMoreProducts) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Load more when user is 200px from bottom
      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.loadMoreProducts();
      }
    }

    updateTotalProducts() {
      const currentCollection = this.getCurrentCollection();
      if (currentCollection) {
        const productGrid = document.querySelector(".product-grid");
        if (productGrid) {
          productGrid.dataset.totalProducts = currentCollection.products.length;
        }
      }
    }

    getCurrentCollection() {
      const activeTab = document.querySelector('.collection-tab[data-active="true"]');
      if (!activeTab) return null;

      const collectionHandle = activeTab.dataset.collectionHandle;
      return this.collectionsData.collections[collectionHandle];
    }

    // async loadMoreProducts() {
    //   if (this.isLoading) return;

    //   this.isLoading = true;
    //   this.showLoadingIndicator();

    //   try {
    //     const currentCollection = this.getCurrentCollection();
    //     if (!currentCollection) {
    //       throw new Error("No collection data available");
    //     }

    //     const startIndex = this.currentPage * this.productsPerPage;
    //     const endIndex = startIndex + this.productsPerPage;
    //     const productsToLoad = currentCollection.products.slice(startIndex, endIndex);

    //     if (productsToLoad.length === 0) {
    //       this.hasMoreProducts = false;
    //       this.showNoMoreProductsIndicator();
    //       this.hideLoadingIndicator();
    //       this.isLoading = false;
    //       return;
    //     }

    //     // Simulate loading delay for better UX
    //     await new Promise((resolve) => setTimeout(resolve, 500));

    //     // Add products to the grid
    //     this.addProductsToGrid(productsToLoad);

    //     this.currentPage++;
    //     this.isLoading = false;
    //     this.hideLoadingIndicator();
    //   } catch (error) {
    //     console.error("Error loading more products:", error);
    //     this.showErrorIndicator();
    //     this.isLoading = false;
    //     this.hideLoadingIndicator();
    //   }
    // }

    addProductsToGrid(products) {
      const productGrid = document.querySelector('.product-grid');
      console.log(productGrid);
      if (!productGrid) return;

      // Get current collection handle
      const activeTab = document.querySelector('.collection-tab[data-active="true"]');
      const collectionHandle = activeTab?.dataset.collectionHandle;
      
      // Store products in collectionsData for lookup
      if (collectionHandle && this.collectionsData.collections[collectionHandle]) {
        this.collectionsData.collections[collectionHandle].products.push(...products);
      }
      
      products.forEach((product, index) => {
        const productCard = this.createProductCard(product, this.currentPage * this.productsPerPage + index + 1);
        productGrid.appendChild(productCard);
      });
    }

    showLoadingIndicator() {
      const loadingIndicator = document.querySelector(".infinite-scroll-loading");
      if (loadingIndicator) {
        loadingIndicator.style.display = "block";
      }
    }

    hideLoadingIndicator() {
      const loadingIndicator = document.querySelector(".infinite-scroll-loading");
      if (loadingIndicator) {
        loadingIndicator.style.display = "none";
      }
    }

    showNoMoreProductsIndicator() {
      const noMoreIndicator = document.getElementById("no-more-products");
      if (noMoreIndicator) {
        noMoreIndicator.style.display = "block";
      }
    }

    hideNoMoreProductsIndicator() {
      const noMoreIndicator = document.getElementById("no-more-products");
      if (noMoreIndicator) {
        noMoreIndicator.style.display = "none";
      }
    }

    showErrorIndicator() {
      const errorIndicator = document.getElementById("infinite-scroll-error");
      if (errorIndicator) {
        errorIndicator.style.display = "block";
      }
    }

    hideErrorIndicator() {
      const errorIndicator = document.getElementById("infinite-scroll-error");
      if (errorIndicator) {
        errorIndicator.style.display = "none";
      }
    }

    resetInfiniteScroll() {
      this.currentPage = 1;
      this.hasMoreProducts = true;
      this.hideLoadingIndicator();
      this.hideNoMoreProductsIndicator();
      this.hideErrorIndicator();
      this.updateTotalProducts();
    }

    retryLoading() {
      this.hideErrorIndicator();
      this.loadMoreProducts();
    }
  }

  // Initialize bundle builder when DOM is loaded
  document.addEventListener("DOMContentLoaded", () => {
    if (document.querySelector(".bundle-builder-section")) {
      new BundleBuilder();
    }
  });

  if (Shopify.designMode) {
    document.addEventListener("shopify:section:unload", new BundleBuilder());
    document.addEventListener("shopify:section:load", new BundleBuilder());
  }
})();